import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EmailAuthProvider,
  getAuth,
  isSignInWithEmailLink,
  linkWithCredential,
  signInWithEmailLink,
  updatePassword,
} from 'firebase/auth';
import firebase from 'firebase/compat/app';
import { toast } from 'react-toastify';
import { firebaseAuth } from '../../providers/AuthProvider';

const STAGE = {
  CONFIRM_EMAIL: 'confirm-email',
  SET_PASSWORD: 'set-password',
  DONE: 'done',
  INVALID: 'invalid',
};

const hasPasswordProvider = (fbUser) =>
  !!fbUser?.providerData?.some((p) => p.providerId === 'password');

const validatePassword = (password) => {
  if (password.length < 12) return 'Password must be at least 12 characters.';
  if (!/[A-Za-z]/.test(password)) return 'Password must include at least one letter.';
  if (!/[0-9]/.test(password)) return 'Password must include at least one number.';
  return null;
};

const FinishSignup = () => {
  const navigate = useNavigate();
  const { refreshClaims } = useContext(firebaseAuth);
  const auth = getAuth();

  const queryParams = new URLSearchParams(window.location.search);
  const queryEmail = (queryParams.get('email') || '').toLowerCase();

  const [stage, setStage] = useState(null);
  const [email, setEmail] = useState(queryEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setStage(STAGE.INVALID);
      return;
    }
    if (queryEmail) {
      void completeSignIn(queryEmail);
    } else {
      setStage(STAGE.CONFIRM_EMAIL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completeSignIn = async (emailToUse) => {
    setBusy(true);
    setError(null);
    try {
      await signInWithEmailLink(auth, emailToUse, window.location.href);
      const fbUser = auth.currentUser;
      if (!fbUser) throw new Error('Sign-in did not produce a user.');
      try {
        const consumeInvite = firebase.functions().httpsCallable('appV2/consumeInvite');
        await consumeInvite({});
      } catch (consumeErr) {
        console.warn('consumeInvite failed:', consumeErr);
      }
      await refreshClaims();
      if (hasPasswordProvider(fbUser)) {
        setStage(STAGE.DONE);
        navigate('/manage/mfa', { replace: true });
      } else {
        setStage(STAGE.SET_PASSWORD);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not complete sign-in.');
      setStage(STAGE.INVALID);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmEmail = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    void completeSignIn(email.trim().toLowerCase());
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const fbUser = auth.currentUser;
    if (!fbUser) {
      setError('Session expired. Please click the email link again.');
      return;
    }
    setBusy(true);
    try {
      if (hasPasswordProvider(fbUser)) {
        await updatePassword(fbUser, password);
      } else {
        const credential = EmailAuthProvider.credential(fbUser.email, password);
        await linkWithCredential(fbUser, credential);
      }
      toast.success('Password set. Now enroll multi-factor authentication.');
      navigate('/manage/mfa', { replace: true });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not set password.');
    } finally {
      setBusy(false);
    }
  };

  if (stage === null || busy) {
    return (
      <div>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">Finishing sign-up…</p>
      </div>
    );
  }

  if (stage === STAGE.INVALID) {
    return (
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
          Invitation link is invalid or expired
        </h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
          Ask your administrator to resend your invitation, or contact{' '}
          <a className="text-zinc-900 hover:underline dark:text-white" href="mailto:support@core12.com">
            support@core12.com
          </a>
          .
        </p>
        {error && (
          <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-500/30 dark:text-red-300 dark:ring-red-500/20">
            {error}
          </div>
        )}
      </div>
    );
  }

  if (stage === STAGE.CONFIRM_EMAIL) {
    return (
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">Confirm your email</h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          To finish signing up, confirm the email address this invitation was sent to.
        </p>
        <form onSubmit={handleConfirmEmail} className="mt-6 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Email address
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
          >
            Continue
          </button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
        Set your password
      </h1>
      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
        Email <span className="text-zinc-900 dark:text-white">{auth.currentUser?.email}</span> verified.
        Choose a password (at least 12 characters, including letters and numbers) to
        finish setting up your account.
      </p>
      <form onSubmit={handleSetPassword} className="mt-6 space-y-4">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white"
          />
        </div>
        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-zinc-700 dark:text-zinc-300"
          >
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
        >
          {busy ? 'Saving…' : 'Save and continue'}
        </button>
        {error && (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-500/30 dark:text-red-300 dark:ring-red-500/20">
            {error}
          </div>
        )}
      </form>
    </div>
  );
};

export default FinishSignup;
